import React from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "context/BrandingProvider";

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h5 className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-2">{title}</h5>
    {children}
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div className="bg-surface-bg rounded-button p-3 text-center">
    <div className={`text-lg font-bold ${color || "text-content-primary"}`}>{value}</div>
    <div className="text-[10px] text-content-tertiary mt-0.5">{label}</div>
  </div>
);

const ActionButton = ({ label, onClick, primary }) => (
  <button
    onClick={onClick}
    className={`w-full py-2.5 rounded-button text-sm font-medium transition-colors ${
      primary
        ? "bg-brand-500 text-white hover:bg-[#6A5EEE]"
        : "bg-surface-hover text-content-primary hover:bg-surface-border"
    }`}
  >
    {label}
  </button>
);

const InsightDetailRenderer = ({ insight, onClose }) => {
  const { currency } = useBranding();
  const navigate = useNavigate();

  const handleNavigate = (route) => {
    onClose();
    const fullRoute = route.startsWith("/portal") ? route : `/portal${route}`;
    navigate(fullRoute);
  };

  if (!insight) return null;

  switch (insight.type) {
    // ========== Revenue Growth ==========
    case "revenue":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Growth Rate" value={`${insight.value}%`} color={insight.value >= 0 ? "text-green-600" : "text-red-500"} />
              <StatCard label="Trend" value={insight.value >= 0 ? "Upward" : "Downward"} color={insight.value >= 0 ? "text-green-600" : "text-red-500"} />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-content-secondary">Compared to previous month</span>
                <span className="font-medium text-content-primary">{insight.value >= 0 ? "+" : ""}{insight.value}%</span>
              </div>
              <div className="w-full bg-surface-border rounded-badge h-2">
                <div
                  className={`h-2 rounded-badge ${insight.value >= 0 ? "bg-green-500" : "bg-red-500"}`}
                  style={{ width: `${Math.min(Math.abs(insight.value) * 2, 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-content-tertiary">Revenue growth is calculated by comparing total payments received this month vs last month.</p>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Full Analytics" onClick={() => handleNavigate("/invoice")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Pending Payments ==========
    case "pending":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Patients at Risk" value={insight.value} color="text-red-500" />
              <StatCard label="Threshold" value={`${currency}5,000+`} color="text-orange-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                {insight.value} patients have outstanding balances exceeding {currency}5,000. These invoices need immediate attention to prevent further accumulation.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-badge bg-red-500" />
                <span className="text-[11px] text-content-secondary">High priority — overdue payments affect cash flow</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="Send Reminder to All" onClick={() => handleNavigate("/invoice")} primary />
              <ActionButton label="Go to Invoice Page" onClick={() => handleNavigate("/invoice")} />
            </div>
          </Section>
        </>
      );

    // ========== Therapy Demand ==========
    case "demand":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Demand Growth" value={`${insight.value}%`} color="text-green-600" />
              <StatCard label="Trend" value="Increasing" color="text-green-600" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                Session bookings for this therapy category increased by {insight.value}% compared to last month. This indicates growing demand.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-badge bg-green-500" />
                <span className="text-[11px] text-content-secondary">Consider increasing staff availability for this therapy</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Appointments" onClick={() => handleNavigate("/appointment")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Cancellation Warning ==========
    case "cancellation":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Cancellation Rate" value={`${insight.value}%`} color="text-red-500" />
              <StatCard label="Status" value="Above Normal" color="text-red-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3 space-y-2">
              <p className="text-xs text-content-secondary leading-relaxed">
                Cancellation/absence rate for this therapy is at {insight.value}%, which exceeds the 8% threshold.
              </p>
              <div className="w-full bg-surface-border rounded-badge h-2">
                <div className="h-2 rounded-badge bg-red-500" style={{ width: `${Math.min(insight.value * 5, 100)}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-content-tertiary">
                <span>0%</span>
                <span className="text-red-400">Threshold: 8%</span>
                <span>20%</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Appointments" onClick={() => handleNavigate("/appointment")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Staff Attendance ==========
    case "attendance":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Attendance Rate" value={`${insight.value}%`} color={insight.value < 60 ? "text-red-500" : "text-orange-500"} />
              <StatCard label="Status" value={insight.value < 60 ? "Critical" : "Below Normal"} color={insight.value < 60 ? "text-red-500" : "text-orange-500"} />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3 space-y-2">
              <p className="text-xs text-content-secondary leading-relaxed">
                Staff attendance is at {insight.value}% this month, which is below the expected 80% threshold.
              </p>
              <div className="w-full bg-surface-border rounded-badge h-2">
                <div
                  className={`h-2 rounded-badge ${insight.value < 60 ? "bg-red-500" : "bg-orange-400"}`}
                  style={{ width: `${insight.value}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-content-tertiary">
                <span>0%</span>
                <span className="text-orange-400">Target: 80%</span>
                <span>100%</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Staff Details" onClick={() => handleNavigate("/employee")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Patient Growth ==========
    case "patient_growth":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Growth Rate" value={`${insight.value > 0 ? "+" : ""}${insight.value}%`} color={insight.value >= 0 ? "text-green-600" : "text-red-500"} />
              <StatCard label="Trend" value={insight.value >= 0 ? "Growing" : "Declining"} color={insight.value >= 0 ? "text-green-600" : "text-red-500"} />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                New patient registrations have {insight.value >= 0 ? "increased" : "decreased"} by {Math.abs(insight.value)}% compared to last month.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-badge ${insight.value >= 0 ? "bg-green-500" : "bg-red-500"}`} />
                <span className="text-[11px] text-content-secondary">
                  {insight.value >= 0 ? "Healthy growth trajectory" : "Review marketing and outreach efforts"}
                </span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View All Patients" onClick={() => handleNavigate("/patient")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Overdue Invoices ==========
    case "overdue":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Overdue Invoices" value={insight.value} color="text-red-500" />
              <StatCard label="Overdue By" value="30+ Days" color="text-orange-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                {insight.value} invoice{insight.value > 1 ? "s have" : " has"} been pending for more than 30 days. Immediate follow-up recommended.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-badge bg-red-500" />
                <span className="text-[11px] text-content-secondary">Outstanding payments impact cash flow and operations</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Overdue Invoices" onClick={() => handleNavigate("/invoice")} primary />
              <ActionButton label="Send Payment Reminders" onClick={() => handleNavigate("/invoice")} />
            </div>
          </Section>
        </>
      );

    // ========== Expense vs Revenue ==========
    case "expense_ratio":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Expense Ratio" value={`${insight.value}%`} color={insight.value > 100 ? "text-red-500" : "text-orange-500"} />
              <StatCard label="Status" value={insight.value > 100 ? "Overspending" : "Monitor"} color={insight.value > 100 ? "text-red-500" : "text-orange-500"} />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3 space-y-2">
              <p className="text-xs text-content-secondary leading-relaxed">
                {insight.value > 100
                  ? "Expenses are exceeding revenue this month. Immediate cost review needed."
                  : `Expenses are at ${insight.value}% of revenue. While within limits, monitoring is recommended.`}
              </p>
              <div className="w-full bg-surface-border rounded-badge h-2">
                <div
                  className={`h-2 rounded-badge ${insight.value > 100 ? "bg-red-500" : "bg-orange-400"}`}
                  style={{ width: `${Math.min(insight.value, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-content-tertiary">
                <span>0%</span>
                <span className="text-orange-400">Healthy: &lt;70%</span>
                <span>100%+</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="Review Expenses" onClick={() => handleNavigate("/expense")} primary />
              <ActionButton label="View Income" onClick={() => handleNavigate("/income")} />
            </div>
          </Section>
        </>
      );

    // ========== Inactive Patients ==========
    case "inactive_patients":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Inactive Patients" value={insight.value} color="text-orange-500" />
              <StatCard label="Inactive Since" value="30+ Days" color="text-orange-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                {insight.value} active patients haven't had any appointments in the last 30 days. Follow-up can help retain them and ensure continuity of care.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-badge bg-orange-400" />
                <span className="text-[11px] text-content-secondary">Patient retention directly impacts long-term revenue</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Patient List" onClick={() => handleNavigate("/patient")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Salary Payout (Payroll module — see UserSalary/SalaryStructure audit) ==========
    case "salary":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Not Yet Paid" value={insight.value} color={new Date().getDate() > 25 ? "text-red-500" : "text-orange-500"} />
              <StatCard label="Paid" value={insight.breakdown?.paid ?? 0} color="text-green-600" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                {insight.value} staff payroll{insight.value > 1 ? "s are" : " is"} not yet paid for this month.
                {new Date().getDate() > 25
                  ? " Month-end is approaching — prioritize payroll."
                  : " Process payroll before month-end."}
              </p>
              {insight.breakdown && (
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-content-secondary">
                  <span>Not Generated: <strong className="text-content-primary">{insight.breakdown.notGenerated}</strong></span>
                  <span>Draft: <strong className="text-content-primary">{insight.breakdown.draft}</strong></span>
                  <span>Approved: <strong className="text-content-primary">{insight.breakdown.approved}</strong></span>
                  <span>Processed: <strong className="text-content-primary">{insight.breakdown.processed}</strong></span>
                  <span>Paid: <strong className="text-content-primary">{insight.breakdown.paid}</strong></span>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-badge ${new Date().getDate() > 25 ? "bg-red-500" : "bg-orange-400"}`} />
                <span className="text-[11px] text-content-secondary">Timely salary builds team trust and morale</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Staff & Payroll" onClick={() => handleNavigate("/salary-management")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Peak Session Hour ==========
    case "peak_hour":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Peak Hour" value={`${insight.value > 12 ? insight.value - 12 : insight.value}:00`} color="text-purple-600" />
              <StatCard label="Sessions" value={insight.value} color="text-purple-600" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                The most booked time slot this month concentrates a significant portion of all sessions. Consider spreading appointments more evenly.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-badge bg-purple-500" />
                <span className="text-[11px] text-content-secondary">Balanced scheduling improves staff efficiency</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Schedule" onClick={() => handleNavigate("/appointment")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Pricing Insights: Discount ==========
    case "discount":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Avg Discount" value={`${insight.value}%`} color="text-orange-500" />
              <StatCard label="Status" value="High" color="text-orange-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                Frequent discounting (avg {insight.value}%) is being applied to this therapy. This reduces effective revenue per session significantly.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-badge bg-orange-400" />
                <span className="text-[11px] text-content-secondary">Review discount policies to protect margins</span>
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="Review Settings" onClick={() => handleNavigate("/settings")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Pricing Insights: Revenue Per Session ==========
    case "pricing":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Rev/Session" value={`${currency}${insight.value?.toLocaleString("en-IN") || 0}`} color="text-orange-500" />
              <StatCard label="vs Average" value="Below" color="text-orange-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">
                Revenue per session for this therapy is significantly below the clinic average. Consider adjusting pricing or reducing discounts.
              </p>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="Review Pricing" onClick={() => handleNavigate("/settings")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Pricing Insights: Retention ==========
    case "retention":
      return (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Single-Month Patients" value={`${insight.value}%`} color="text-red-500" />
              <StatCard label="Retention" value="Low" color="text-red-500" />
            </div>
          </Section>
          <Section title="Breakdown">
            <div className="bg-surface-bg rounded-button p-3 space-y-2">
              <p className="text-xs text-content-secondary leading-relaxed">
                {insight.value}% of patients visited only 1 month in the last 3 months. Low retention indicates patients are not returning for follow-ups.
              </p>
              <div className="w-full bg-surface-border rounded-badge h-2">
                <div className="h-2 rounded-badge bg-red-500" style={{ width: `${insight.value}%` }} />
              </div>
            </div>
          </Section>
          <Section title="Actions">
            <div className="space-y-2">
              <ActionButton label="View Patient List" onClick={() => handleNavigate("/patient")} primary />
            </div>
          </Section>
        </>
      );

    // ========== Default Fallback ==========
    default:
      return (
        <>
          <Section title="Summary">
            <div className="bg-surface-bg rounded-button p-3">
              <p className="text-xs text-content-secondary leading-relaxed">{insight.message}</p>
            </div>
          </Section>
          {insight.actionRoute && (
            <Section title="Actions">
              <ActionButton label="View Details" onClick={() => handleNavigate(insight.actionRoute)} primary />
            </Section>
          )}
        </>
      );
  }
};

export default InsightDetailRenderer;
